import {some} from '@danwang/option';
import {useRecordBalanceAnchorMutation} from '@transactions/graphql';
import * as Date from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import {toStoredBalance} from 'src/account/balanceDisplay';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import {Modal} from 'src/Modal';
import type {Option} from '@danwang/option';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

export type RecordBalanceAnchorModalUserProps = {
  accountId: string;
  /**
   * The account's currency (default USD). Drives the amount input's unit —
   * a JPY wallet anchor is entered in whole yen.
   */
  currency?: string;
  /**
   * Credit card balances are stored negative (liability convention). When true,
   * the user enters a positive "amount owed" and the modal negates it before
   * saving so the stored observation matches the storage convention.
   */
  isCreditCard?: boolean;
  /** Pre-fill a date (e.g. when promoting a synced reading to an anchor) */
  initialDate?: DateTime;
  /** Pre-fill a balance value, in display units (positive "owed" for credit cards) */
  initialBalance?: Money;
  /**
   * Called with the saved anchor's id. Recording on a date that has a synced
   * Plaid reading creates an anchor alongside it (the reading is kept
   * as-is), so callers wanting to show the result must use this id rather
   * than the reading's.
   */
  onSuccess?: (anchorId: string) => void;
};

export const RecordBalanceAnchorModal = (
  props: RecordBalanceAnchorModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const [recordAnchor, {loading}] = useRecordBalanceAnchorMutation();
  const [date, setDate] = React.useState<DateTime>(
    props.initialDate ?? Date.today(),
  );
  // Default to a concrete zero rather than `none` so the field, which already
  // displays "0.00" on open, can be submitted as a zero balance without the
  // user having to type a digit to register a value.
  const [balance, setBalance] = React.useState<Option<Money>>(
    some(props.initialBalance ?? Money.zero(props.currency ?? 'USD')),
  );
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (balance.isEmpty) {
      setError('Please enter a balance amount');
      return;
    }

    // The user enters the display value ("amount owed" for credit cards, with
    // a negative entry meaning an overpayment/statement credit); convert to
    // the stored sign convention.
    const storedBalance = toStoredBalance(
      balance.get().toInt().amount,
      props.isCreditCard ?? false,
    );

    try {
      const result = await recordAnchor({
        variables: {
          accountId: props.accountId,
          date: date.toISODate() ?? '',
          balance: storedBalance,
          source: 'manual',
          note: note.trim() || undefined,
        },
      });
      const saved = result.data?.recordBalanceAnchor;
      if (saved) {
        props.onSuccess?.(saved._id);
      }
      props.onRequestClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to record balance',
      );
    }
  };

  const isValid = balance.nonEmpty;

  const footer = (
    <>
      <Button
        variant="outline"
        disabled={loading}
        onClick={props.onRequestClose}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={loading || !isValid}
        type="submit"
        form={formId}
      >
        Save
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      header="Record balance"
      footer={footer}
    >
      <form
        id={formId}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-4"
      >
        <InputGroup label="Date">
          <DatePicker selectedDate={date} onChange={setDate} />
        </InputGroup>
        <InputGroup label={props.isCreditCard ? 'Amount owed' : 'Balance'}>
          <AmountInput
            value={balance}
            onChange={setBalance}
            autoFocus
            currency={props.currency}
          />
        </InputGroup>
        <InputGroup label="Note (optional)">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Statement closing balance"
            className="border-border bg-background text-foreground focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:ring-1 focus:outline-none"
          />
        </InputGroup>
        {error && <div className="text-destructive text-sm">{error}</div>}
      </form>
    </Modal>
  );
};
